// Lixeira de oscilografias e eventos de todos os grupos, no cartão Arquivos do diagnóstico
// (só o admin da plataforma). Os gestores têm a lixeira das próprias usinas nas telas de
// oscilografia e eventos.
//
// "Remover agora" vence o prazo do arquivo; a limpeza da ingestão apaga o registro e o bruto
// em até 5 min (a API não enxerga o volume dos brutos).

import { useQuery } from "@tanstack/react-query";
import { RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { Alert, Badge, Button, Modal, Spinner, Table, cx } from "../../components/ui";
import { api } from "../../lib/api";
import { useAction } from "../../lib/hooks";

interface TrashFile {
  id: number;
  kind: "osc" | "evt";
  name: string;
  raw_size: number | null;
  deleted_at: string;
  deleted_by_name: string | null;
  purge_at: string;
  purging: boolean;
  regulator_name: string;
  site_name: string;
  group_name: string;
}

const size = (b: number | null) =>
  b === null ? "—" : b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 ** 2).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
const fmt = (iso: string) => new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

export function AdminTrash() {
  const q = useQuery({
    queryKey: ["admin-trash"],
    queryFn: () => api.get<{ trashDays: number; purgeEveryMinutes: number; files: TrashFile[] }>("/api/admin/trash"),
    refetchInterval: 30_000,
  });
  const { run, busy, error } = useAction([["admin-trash"], ["diagnostics"], ["osc"], ["soe-files"]]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirm, setConfirm] = useState<"selected" | "all" | null>(null);

  const files = q.data?.files ?? [];
  // Os que já venceram estão sendo removidos: não se selecionam nem voltam.
  const pending = files.filter((f) => !f.purging);
  const chosen = pending.filter((f) => selected.has(f.id));
  const toggle = (id: number) => setSelected((s) => (s.has(id) ? new Set([...s].filter((x) => x !== id)) : new Set(s).add(id)));

  async function restore() {
    if (await run(() => api.post("/api/admin/trash/restore", { ids: chosen.map((f) => f.id) }))) setSelected(new Set());
  }

  async function purge() {
    const body = confirm === "all" ? { all: true } : { ids: chosen.map((f) => f.id) };
    if (await run(() => api.post("/api/admin/trash/purge", body))) {
      setConfirm(null);
      setSelected(new Set());
    }
  }

  if (q.isLoading) return <Spinner />;
  const total = files.reduce((n, f) => n + (f.raw_size ?? 0), 0);

  return (
    <div className="border-t border-line pt-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">Lixeira</h3>
        <span className="text-xs text-muted">
          {files.length ? `${files.length} arquivo(s) · ${size(total)}` : "vazia"} · remoção automática após {q.data?.trashDays ?? 30} dias
        </span>
        <div className="ml-auto flex gap-2">
          {chosen.length > 0 ? (
            <>
              <Button size="sm" variant="secondary" loading={busy} onClick={restore}>
                <RotateCcw className="size-3.5" aria-hidden /> Restaurar {chosen.length}
              </Button>
              <Button size="sm" variant="danger" onClick={() => setConfirm("selected")}>
                <Trash2 className="size-3.5" aria-hidden /> Remover {chosen.length} agora
              </Button>
            </>
          ) : (
            pending.length > 0 && (
              <Button size="sm" variant="secondary" onClick={() => setConfirm("all")}>
                <Trash2 className="size-3.5" aria-hidden /> Esvaziar lixeira
              </Button>
            )
          )}
        </div>
      </div>

      {error && (
        <div className="mb-2">
          <Alert kind="error">{error}</Alert>
        </div>
      )}

      {files.length > 0 && (
        <div className="max-h-96 overflow-auto">
          <Table>
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    aria-label="Selecionar todos"
                    checked={pending.length > 0 && chosen.length === pending.length}
                    onChange={() => setSelected(chosen.length === pending.length ? new Set() : new Set(pending.map((f) => f.id)))}
                    className="accent-[var(--brand)]"
                  />
                </th>
                <th>Arquivo</th>
                <th>Onde</th>
                <th>Apagado</th>
                <th>Remoção</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id} className={cx(selected.has(f.id) && "bg-danger/5")}>
                  <td>
                    {!f.purging && (
                      <input
                        type="checkbox"
                        aria-label={`Selecionar ${f.name}`}
                        checked={selected.has(f.id)}
                        onChange={() => toggle(f.id)}
                        className="accent-[var(--brand)]"
                      />
                    )}
                  </td>
                  <td className="text-xs">
                    <code className="font-mono">{f.name}</code>
                    <p className="text-muted">
                      {f.kind === "osc" ? "oscilografia" : "eventos"} · {size(f.raw_size)}
                    </p>
                  </td>
                  <td className="text-xs text-muted">
                    {f.group_name} › {f.site_name} › {f.regulator_name}
                  </td>
                  <td className="whitespace-nowrap text-xs">
                    {fmt(f.deleted_at)}
                    {f.deleted_by_name && <p className="text-muted">por {f.deleted_by_name}</p>}
                  </td>
                  <td className="whitespace-nowrap text-xs text-muted">{f.purging ? <Badge tone="warn">removendo…</Badge> : fmt(f.purge_at)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {confirm && (
        <Modal
          open
          onClose={() => setConfirm(null)}
          title={confirm === "all" ? "Esvaziar a lixeira" : `Remover ${chosen.length} arquivo(s) agora`}
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                Cancelar
              </Button>
              <Button variant="danger" loading={busy} onClick={purge}>
                <Trash2 className="size-4" aria-hidden /> Remover de vez
              </Button>
            </>
          }
        >
          <p className="text-sm">
            {confirm === "all" ? `Os ${pending.length} arquivos da lixeira, de todos os grupos,` : "Os arquivos selecionados"} são removidos de vez, com o
            arquivo bruto, em até {q.data?.purgeEveryMinutes ?? 5} minutos. Não dá para restaurar depois.
          </p>
        </Modal>
      )}
    </div>
  );
}
